import { createFileRoute, redirect } from '@tanstack/react-router'
import thai2englishApi from 'api/thai2english-api'
import { dictionaryRouteFor, englishRoute, thaiRoute } from 'lib/dictionary-routes'

// Legacy inbound URL from earlier ASP.NET site version (e.g. /search.aspx?q=นั่งชมวิว).
//
// These links are the site's oldest, and most of them name a single headword —
// the old site was a dictionary. So this resolves the query against the
// dictionary and sends it straight to the entry's canonical URL, rather than
// bouncing it off `/?q=`, which is `noindex` and `Disallow`ed and therefore a
// dead end for anything following the link.
//
// Only an entry gets a 301. A query with no entry is a sentence, a typo or
// junk: `/?q=` is the right destination for a reader, but the dictionary keeps
// growing, so today's miss may be tomorrow's entry and a permanent redirect
// would outlive the fix. A failed lookup is not a miss at all — 301-ing an
// outage would pin a real headword to the dead end for as long as the redirect
// stays cached (the same trap `redirectToBestMatch` documents).
export const Route = createFileRoute('/search.aspx')({
   validateSearch: (search: Record<string, unknown>): Record<string, unknown> => search,
   beforeLoad: async ({ search }) => {
      const query = typeof search.q === 'string' ? search.q.trim() : ''
      // Nothing to resolve — the bare legacy URL has always meant the homepage.
      if (!query) throw redirect({ to: '/', search, statusCode: 301 })

      const toQueryPage = () => redirect({ to: '/', search, statusCode: 302 })

      // A bare entry id or a sentence can never be an exact match, so skip the
      // lookup entirely: `/?q=` is where both are handled. `dictionaryRouteFor`
      // caps the Latin branch at four words but guesses an entry for any Thai
      // text — Thai is written unspaced, so whitespace there means a phrase.
      const guess = dictionaryRouteFor(query)
      if (!guess) throw toQueryPage()
      if (guess.to === '/thai/$word' && /\s/.test(query)) throw toQueryPage()

      const results = await thai2englishApi.searchDictionary(query)
      if (results.searchFailed) throw toQueryPage()

      // Prefer the language the query is written in, matching how
      // `redirectToBestMatch` breaks the same tie.
      const { thaiExactMatch: thai, engExactMatch: eng } = results
      const ordered =
         guess.to === '/thai/$word'
            ? ([
                 [thai, thaiRoute],
                 [eng, englishRoute],
              ] as const)
            : ([
                 [eng, englishRoute],
                 [thai, thaiRoute],
              ] as const)
      for (const [match, route] of ordered) {
         if (match) throw redirect({ ...route(match.word), statusCode: 301 })
      }

      throw toQueryPage()
   },
})
